import * as os from "os";
import * as path from "path";

import { ensureDir, move, pathExists, readFile, rename, rmdir, stat, writeFile, writeJson } from "fs-extra";

import { getLastStableEngineVersion, getLastStableGeneratorVersion } from "../../BuildTasks/PublishedVersionUtils";
import { readFormConfig } from "../../BuildTasks/ResolveFormInfo";
import { ConsoleApplicationContext } from "../../Commons/ConsoleApplication/ConsoleApplicationContext";
import { globAsync, isDirectoryExists } from "../../Commons/FsUtils";
import { InvalidCommandLineArgumentsError } from "../../Commons/Options/CommandBasedOptionsParser";
import { WellKnownDirectories } from "../../../../Common/WellKnownDirectories";

import { InitFormOptions } from "./InitFormOptions";

export async function runInitForm(context: ConsoleApplicationContext, options: InitFormOptions): Promise<void> {
    const logger = context.logger;
    if (options.gfvs == undefined) {
        throw new InvalidCommandLineArgumentsError("Не указана GVF для создания", "");
    }

    let repositoryRoot = process.cwd();
    const templateDir = path.join(__dirname, "EmptyFormTemplate");
    if (
        path.basename(process.cwd()) === "candy.farm" ||
        path.basename(process.cwd()) === "candy.diadoc.farm" ||
        path.basename(process.cwd()) === "candy.rpn.farm" ||
        path.basename(process.cwd()) === "candy.farm"
    ) {
        repositoryRoot = path.normalize(path.join(repositoryRoot, ".."));
    }

    if (!(await isDirectoryExists(path.join(repositoryRoot, WellKnownDirectories.FormsDirectory)))) {
        throw new Error("Текущая директория не похожа на каталог с исходниками форм");
    }

    for (const gfv of options.gfvs) {
        const gfvLogger = logger.withPrefix(gfv);
        const { g, f } = splitGfv(gfv);
        const formPath = path.join(
            repositoryRoot,
            WellKnownDirectories.FormsDirectory,
            g.toString(),
            f.toString().padStart(3, "0"),
            gfv.toString(),
            WellKnownDirectories.CandySubdirectoryName
        );
        if (await pathExists(formPath)) {
            const errorMessage = `Directory for '${gfv}' already exists in Candy folder.`;
            gfvLogger.error(errorMessage);
            throw new Error(errorMessage);
        }
        const tempOutputDir = path.join(__dirname, ".tmp", gfv);
        if (await pathExists(tempOutputDir)) {
            await rmdir(tempOutputDir, { recursive: true });
        }
        gfvLogger.log(`Creating directory with sources from template`);
        await ensureDir(tempOutputDir);
        const highlanderFarmPath = path.join(repositoryRoot, "highlander.farm", "Forms", gfv);
        const hlRngPath1 = path.join(highlanderFarmPath, `${gfv}.схема.rng.xml`);
        const hlRngPath2 = path.join(highlanderFarmPath, "Highlander", `${gfv}.схема.rng.xml`);
        for (const relativeFilePath of await globAsync("**/*", { cwd: templateDir })) {
            const targetRelativePath = relativeFilePath.replace(/{gfv}/g, gfv);
            gfvLogger.verbose(`Processing template path ${relativeFilePath}`);
            if ((await stat(path.join(templateDir, relativeFilePath))).isDirectory()) {
                await ensureDir(path.join(tempOutputDir, targetRelativePath));
            } else {
                if (
                    options.hlrng &&
                    targetRelativePath === `schemas/${gfv}.0.rng.xml` &&
                    ((await pathExists(hlRngPath1)) || (await pathExists(hlRngPath2)))
                ) {
                    const existsHlRngPath = (await pathExists(hlRngPath1)) ? hlRngPath1 : hlRngPath2;
                    gfvLogger.log(`Copy schema rng file from highlander.farm.`);
                    const content = await readFile(existsHlRngPath, "utf-8");
                    const targetContent = content.replace(/{gfv}/g, gfv);
                    await writeFile(path.join(tempOutputDir, targetRelativePath), targetContent, "utf-8");
                } else {
                    if (targetRelativePath.includes("helpers/addPril1.txt")) {
                        //при копировании js файлов babel насовывает use strict, обошли таким путем
                        const content = await readFile(path.join(templateDir, relativeFilePath), "utf-8");
                        await writeFile(
                            path.join(tempOutputDir, targetRelativePath.replace(".txt", ".js")),
                            content,
                            "utf-8"
                        );
                    } else {
                        const content = await readFile(path.join(templateDir, relativeFilePath), "utf-8");
                        const targetContent = content.replace(/{gfv}/g, gfv);
                        await writeFile(path.join(tempOutputDir, targetRelativePath), targetContent, "utf-8");
                    }
                }
            }
            gfvLogger.verbose(`Processed template path ${relativeFilePath}`);
        }
        const formJsonPath = path.join(tempOutputDir, WellKnownDirectories.FormJsonFileName);
        const formConfig = await readFormConfig(formJsonPath);
        formConfig.dependencies = {
            engine: await getLastStableEngineVersion(),
            generator: await getLastStableGeneratorVersion(),
        };
        gfvLogger.log(`Set last stable version ${formConfig.dependencies.engine}`);
        await writeJson(formJsonPath, formConfig, { spaces: 2 });
        gfvLogger.log(`Moving prepared sources to '${repositoryRoot}'`);
        await moveDirectory(tempOutputDir, formPath);
        gfvLogger.log(`Form creating completed. Path: '${formPath}'`);
    }
}

async function moveDirectory(sourcePath: string, targetPath: string): Promise<void> {
    const basedir = path.dirname(targetPath);
    await ensureDir(basedir);
    if (os.platform() !== "win32") {
        await rename(sourcePath, targetPath);
    } else {
        if (sourcePath[0] === targetPath[0]) {
            await rename(sourcePath, targetPath);
        } else {
            await move(sourcePath, targetPath);
        }
    }
}

function splitGfv(gfv: string | number): { g: number; f: number; v: number } {
    const gfvNumber = typeof gfv === "string" ? Number(gfv) : gfv;
    return {
        g: Math.floor(gfvNumber / 100000),
        f: Math.floor((gfvNumber % 100000) / 100),
        v: gfvNumber % 100,
    };
}
